import React from "react";
import { useState, useEffect, useRef } from "react";
import { ThemeToggle } from "./theme-toggle";
import { ProjectSwitcher } from "./ProjectSwitcher";
import { Button } from "./ui/button";
import {
  Copy,
  FileText,
  Link,
  Settings,
  Edit,
  CheckCircle,
  Loader2,
} from "lucide-react";

interface HeaderProps {
  showHero?: boolean;
  title?: string;
  description?: string;
  onCopyConfig?: () => void;
  onCopyURL?: () => void;
  onGeneratePDF?: () => void;
  onGenerateInvestorReportPDF?: () => void;
  onLoadConfig?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  showCopied?: boolean;
  showUrlCopied?: boolean;
  onOpenSettings?: () => void;
  onProjectNameChange?: (name: string) => void;
  projectName?: string;
  investmentAmount?: number;
  roi?: number;
  breakEvenMonth?: number;
  currentValuation?: number;
  isSyncing?: boolean;
  lastSynced?: Date | null;
  onOpenGlobalSettings?: () => void;
  // Navigation props
  activeTab?: "overview" | "calculator" | "costing" | "people" | "packages" | "unit-economics";
  onTabChange?: (tab: "overview" | "calculator" | "costing" | "people" | "packages" | "unit-economics") => void;
}

export function Header({
  showHero = false,
  title,
  description,
  onCopyConfig,
  onCopyURL,
  onGeneratePDF,
  onGenerateInvestorReportPDF,
  onLoadConfig,
  showCopied = false,
  showUrlCopied = false,
  onOpenSettings,
  onProjectNameChange,
  projectName: initialProjectName = "My SaaS Startup",
  investmentAmount,
  roi,
  breakEvenMonth,
  currentValuation,
  isSyncing = false,
  lastSynced,
  onOpenGlobalSettings,
  activeTab,
  onTabChange,
}: HeaderProps) {
  const urlParamProcessed = useRef(false);

  // Function to update URL with current project name
  const updateURL = (name: string) => {
    const url = new URL(window.location.href);
    if (name && name !== "My SaaS Startup") {
      url.searchParams.set("projectName", name);
    } else {
      url.searchParams.delete("projectName");
    }
    window.history.replaceState({}, "", url.toString());
  };

  useEffect(() => {
    // Only process URL parameter once on mount to avoid interfering with other functionality
    if (!urlParamProcessed.current) {
      const urlParams = new URLSearchParams(window.location.search);
      const urlProjectName = urlParams.get("projectName");

      // Basic input validation and sanitization
      if (urlProjectName && urlProjectName !== initialProjectName) {
        // Limit length and remove potentially harmful characters
        const sanitizedName = urlProjectName
          .trim()
          .substring(0, 50) // Max 50 characters
          .replace(/[<>\"'&]/g, ""); // Remove potentially dangerous characters

        if (sanitizedName && sanitizedName !== initialProjectName) {
          onProjectNameChange?.(sanitizedName);
        }
      }
      urlParamProcessed.current = true;
    }
  }, [initialProjectName, onProjectNameChange]);

  // Update URL when project name prop changes (after initial load)
  useEffect(() => {
    if (urlParamProcessed.current) {
      updateURL(initialProjectName);
    }
  }, [initialProjectName]);

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border bg-background">
      {/* Header Bar */}
      <div className="container flex h-16 items-center justify-between px-6">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <a href="/" className="flex items-center space-x-2">
              <div className="h-8 w-8 rounded-md bg-primary flex items-center justify-center">
                <span className="text-primary-foreground font-bold text-sm">
                  S
                </span>
              </div>
              <div>
                {onProjectNameChange ? (
                  <input
                    type="text"
                    value={initialProjectName}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Limit length and basic sanitization
                      if (value.length <= 50) {
                        onProjectNameChange?.(value.replace(/[<>\"'&]/g, ""));
                      }
                    }}
                    className="bg-transparent border-none outline-none text-xl font-bold text-foreground placeholder:text-muted-foreground"
                    placeholder="Project Name"
                  />
                ) : (
                  <h1 className="text-xl font-bold text-foreground">
                    {initialProjectName}
                  </h1>
                )}
              </div>
            </a>
          </div>

          {/* Navigation Links */}
          <nav className="flex items-center space-x-4">
            {onTabChange && activeTab && (
              <>
                <button
                  onClick={() => onTabChange("overview")}
                  className={`text-sm font-medium transition-colors ${
                    activeTab === "overview"
                      ? "text-primary"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  Overview
                </button>
                <button
                  onClick={() => onTabChange("calculator")}
                  className={`text-sm font-medium transition-colors ${
                    activeTab === "calculator"
                      ? "text-primary"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  Investment Calculator
                </button>
                <button
                  onClick={() => onTabChange("unit-economics")}
                  className={`text-sm font-medium transition-colors ${
                    activeTab === "unit-economics"
                      ? "text-primary"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  Unit Economics
                </button>
                <button
                  onClick={() => onTabChange("packages")}
                  className={`text-sm font-medium transition-colors ${
                    activeTab === "packages"
                      ? "text-primary"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  Pricing
                </button>
                <button
                  onClick={() => onTabChange("costing")}
                  className={`text-sm font-medium transition-colors ${
                    activeTab === "costing"
                      ? "text-primary"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  Cost Analysis
                </button>
                <button
                  onClick={() => onTabChange("people")}
                  className={`text-sm font-medium transition-colors ${
                    activeTab === "people"
                      ? "text-primary"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  Team & People
                </button>
              </>
            )}
          </nav>
        </div>

        <div className="flex items-center gap-2">
          <ProjectSwitcher />
          <ThemeToggle />
        </div>
      </div>

      {/* Hero Section */}
      {showHero && (
        <div className="border-t bg-background/50">
          <div className="container px-6 py-6">
            <div className="flex justify-between items-center">
              {/* Investment Metrics */}
              <div className="flex items-center space-x-6">
                <div className="text-center">
                  <div className="text-xs font-medium text-muted-foreground mb-1">
                    Investment
                  </div>
                  <div className="text-md font-light text-foreground">
                    ₹{(investmentAmount || 0).toLocaleString()}
                  </div>
                </div>
                <div className="text-left">
                  <div className="text-xs font-medium text-muted-foreground mb-1">
                    Current Valuation
                  </div>
                  <div className="text-md font-light text-purple-600 dark:text-purple-400">
                    ₹{(currentValuation || 0).toLocaleString()}
                  </div>
                </div>
                <div className="text-left">
                  <div className="text-xs font-medium text-muted-foreground mb-1">
                    ROI
                  </div>
                  <div className="text-md font-light text-green-600 dark:text-green-400">
                    {(roi || 0).toFixed(1)}%
                  </div>
                </div>
                <div className="text-left">
                  <div className="text-xs font-medium text-muted-foreground mb-1">
                    Break-even
                  </div>
                  <div className="text-md font-light text-blue-600 dark:text-blue-400">
                    {breakEvenMonth ? `Month ${breakEvenMonth}` : "N/A"}
                  </div>
                </div>
              </div>

              {/* Settings Button */}
              <div className="flex items-center gap-4">
                {/* Action Buttons */}
                <div className="flex items-center gap-1">
                  {onCopyConfig && (
                    <Button
                      onClick={onCopyConfig}
                      variant="outline"
                      size="icon"
                      title={showCopied ? "Copied!" : "Copy Config"}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  )}
                  {onCopyURL && (
                    <Button
                      onClick={onCopyURL}
                      variant="outline"
                      size="icon"
                      title={showUrlCopied ? "URL Copied!" : "Copy URL"}
                    >
                      <Link className="w-4 h-4" />
                    </Button>
                  )}
                  {onGeneratePDF && (
                    <Button
                      onClick={onGeneratePDF}
                      variant="outline"
                      size="icon"
                      title="Export Investment Plan PDF"
                    >
                      <FileText className="w-4 h-4" />
                    </Button>
                  )}
                  {onGenerateInvestorReportPDF && (
                    <Button
                      onClick={onGenerateInvestorReportPDF}
                      variant="outline"
                      size="icon"
                      title="Download Investor Report PDF"
                    >
                      <FileText className="w-4 h-4" />
                    </Button>
                  )}
                  {onLoadConfig && (
                    <label
                      className="flex items-center justify-center w-9 h-9 border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md transition-colors cursor-pointer"
                      title="Load Config"
                    >
                      <FileText className="w-4 h-4" />
                      <input
                        type="file"
                        accept=".json"
                        onChange={onLoadConfig}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>

                {onOpenGlobalSettings && (
                  <Button
                    onClick={onOpenGlobalSettings}
                    variant="outline"
                    size="sm"
                    className="flex items-center space-x-2"
                  >
                    <Settings className="w-4 h-4" />
                    <span className="hidden sm:inline">Global Settings</span>
                  </Button>
                )}

                {onOpenSettings && (
                  <Button
                    onClick={onOpenSettings}
                    variant="outline"
                    size="sm"
                    className="flex items-center space-x-2"
                    disabled={isSyncing}
                  >
                    {isSyncing ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Edit className="w-4 h-4" />
                    )}
                    <span className="hidden sm:inline">Project Settings</span>
                  </Button>
                )}

                {/* Sync Status Indicator */}
                {lastSynced && !isSyncing && (
                  <div className="flex items-center space-x-1 text-xs text-green-600 dark:text-green-400">
                    <CheckCircle className="w-3 h-3" />
                    <span className="hidden sm:inline">Synced</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
